import React, { useEffect } from "react";
import { translateUi } from "./i18n/index";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { AlertTriangle, X } from "./icons";
import { GbrModalBackdrop } from "./GbrModalBackdrop";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type ConfirmActionTone = "danger" | "warning";

export function ConfirmActionModal({
  open,
  dark,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  busy = false,
  busyLabel,
  onClose,
  onConfirm,
}: {
  open: boolean;
  dark: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmActionTone;
  busy?: boolean;
  busyLabel?: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const isDanger = tone === "danger";
  const headerCls = isDanger
    ? dark
      ? "bg-rose-950/35"
      : "bg-rose-50/90"
    : dark
      ? "bg-amber-950/35"
      : "bg-amber-50/90";
  const iconWrapCls = isDanger
    ? dark
      ? "bg-rose-900/60 text-rose-200"
      : "bg-rose-100 text-rose-700"
    : dark
      ? "bg-amber-900/60 text-amber-200"
      : "bg-amber-100 text-amber-800";
  const confirmCls = isDanger
    ? "bg-rose-600 hover:bg-rose-500 shadow-lg shadow-rose-600/25"
    : "bg-amber-600 hover:bg-amber-500 shadow-lg shadow-amber-600/25";

  const paragraphs =
    typeof description === "string"
      ? description
          .split(/\n+/)
          .map((line) => line.trim())
          .filter(Boolean)
      : null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-action-title"
        className={cn(
          "relative w-full max-w-md overflow-hidden rounded-t-3xl border shadow-2xl sm:rounded-3xl",
          dark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900",
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <GbrModalBackdrop opacity={dark ? 0.07 : 0.05} />
        <div className="relative">
          <div className={cn("px-5 pt-5 sm:px-6 sm:pt-6", headerCls)}>
            <div className="flex items-start gap-3">
              <div className={cn("mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", iconWrapCls)}>
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="confirm-action-title" className="text-lg font-bold leading-snug">
                  {title}
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className={cn(
                  "rounded-lg p-2 transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                  dark ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-white/80",
                )}
                aria-label={translateUi("Close")}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 space-y-2 pb-5">
              {paragraphs
                ? paragraphs.map((line, index) => (
                    <p key={index} className={cn("text-sm leading-relaxed", dark ? "text-slate-300" : "text-slate-600")}>
                      {line}
                    </p>
                  ))
                : description}
            </div>
          </div>
          <div className="flex flex-col-reverse gap-2 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={cn(
                "min-h-[44px] rounded-xl px-4 text-sm font-semibold disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100",
              )}
            >
              {cancelLabel || translateUi("Cancel")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onConfirm()}
              className={cn(
                "min-h-[44px] rounded-xl px-5 text-sm font-semibold text-white transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                confirmCls,
              )}
            >
              {busy ? busyLabel || translateUi("Working…") : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
